/**
 * State for one photo's "Text" read. Exits are explicit and never confused:
 *   idle     — not asked
 *   running  — waiting on the server
 *   ready    — words found
 *   none     — the server read the photo successfully and it has no text ("No text found")
 *   error    — the read FAILED (never shown as "No text found"); `retry` runs it again
 * Results are remembered per photo for the life of the page, so toggling Text off and on, or
 * stepping away and back, does not call the server again.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { OcrResponse } from './ocrTypes';

export type OcrPhase = 'idle' | 'running' | 'ready' | 'none' | 'error';

const MAX_REMEMBERED = 40;
const remembered = new Map<string, OcrResponse>();
/**
 * Reads on their way back, by photo. A second ask for a photo already being read JOINS that read
 * instead of starting another (toggling Text off and on, or entering/leaving edit mode, mid-read
 * would otherwise cost a second server read), and the answer is remembered even if the viewer that
 * asked has since moved on.
 */
const inFlight = new Map<string, Promise<OcrResponse>>();

function readOnce(key: string, image: OcrImage): Promise<OcrResponse> {
  const existing = inFlight.get(key);
  if (existing) return existing;
  const p = import('./readPhotoText')
    .then((m) => m.readPhotoText(image))
    .then((r) => {
      remember(key, r);
      return r;
    })
    .finally(() => inFlight.delete(key));
  inFlight.set(key, p);
  return p;
}

function remember(key: string, value: OcrResponse) {
  if (remembered.size >= MAX_REMEMBERED) remembered.delete(remembered.keys().next().value as string);
  remembered.set(key, value);
}

/** Test seam: forget everything remembered. */
export function clearOcrMemory() {
  remembered.clear();
  inFlight.clear();
}

export interface OcrImage {
  src: string;
  ocrPath?: string;
}

export function useOcrText(image: OcrImage | undefined, requested: boolean) {
  const key = image ? (image.ocrPath ?? image.src) : '';
  const [phase, setPhase] = useState<OcrPhase>('idle');
  const [result, setResult] = useState<OcrResponse | null>(null);
  const [attempt, setAttempt] = useState(0);
  const latestKey = useRef(key);
  latestKey.current = key;

  useEffect(() => {
    if (!requested || !image || !key) {
      setPhase('idle');
      setResult(null);
      return undefined;
    }
    const hit = remembered.get(key);
    if (hit) {
      setResult(hit);
      setPhase(hit.words.length > 0 ? 'ready' : 'none');
      return undefined;
    }
    let live = true;
    setPhase('running');
    setResult(null);
    (async () => {
      try {
        const r = await readOnce(key, image);
        if (!live || latestKey.current !== key) return;
        setResult(r);
        setPhase(r.words.length > 0 ? 'ready' : 'none');
      } catch {
        if (live) setPhase('error');
      }
    })();
    return () => {
      live = false;
    };
    // `image` identity changes every render of the host; the key is its identity for this purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, requested, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { phase, result, retry };
}
